import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/Card';
import Button from '../components/Button';
import NavBar from '../components/NavBar';
import EducationalBanner from '../components/EducationalBanner';
import { useFirstVisit } from '../hooks/useFirstVisit';

// Keyword responses
const responses = {
  // Crisis keywords (highest priority)
  "hurt|suicide|kill|die|hopeless": "I'm really concerned about you. You're not alone. Please reach out to Crisis Text Line: Text HOME to 741741",
  
  // Stress/Anxiety responses  
  "stressed|anxious|worried|overwhelmed": "I hear you're feeling stressed. Let's try a 3-minute breathing exercise to help you feel more centered.",
  
  // Positive responses
  "good|great|awesome|happy": "That's wonderful! Keep up the positive momentum. Would you like to set a wellness goal for today?",
  
  // Default supportive
  "default": "Thanks for sharing with me. How would you rate your mood today on a scale of 1-10?"
};

// Mood options
const moodOptions = [
  { emoji: '😊', label: 'Great', range: '1-3' },
  { emoji: '😐', label: 'Okay', range: '4-7' },
  { emoji: '😔', label: 'Struggling', range: '8-10' }
];

// Action buttons
const actionButtons = [
  { text: "Try Breathing Exercise", path: "/biofeedback" },
  { text: "View Daily Quests", path: "/quest" },
  { text: "Talk to Peer Mentor", path: "/peer" }
];

function BridgeBotChat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "Hi there! I'm BridgeBot, your mental wellness companion. How are you feeling today?", 
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [sessionCount] = useState(2); // This would come from localStorage in a real app
  const { isFirstVisit, setVisited } = useFirstVisit('bridgebot');
  const [showEducationalBanner, setShowEducationalBanner] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to top on mount and check for first visit
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (isFirstVisit) {
      setShowEducationalBanner(true);
    }
  }, [isFirstVisit]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Find appropriate response based on keywords
  const findResponse = (text) => {
    const lowerText = text.toLowerCase();
    
    // Check for crisis keywords first (highest priority)
    if (lowerText.match(new RegExp(Object.keys(responses)[0]))) {
      return {
        text: responses["hurt|suicide|kill|die|hopeless"],
        isCrisis: true
      };
    }
    
    // Check for stress/anxiety keywords
    if (lowerText.match(new RegExp(Object.keys(responses)[1]))) {
      return {
        text: responses["stressed|anxious|worried|overwhelmed"],
        showActions: true
      };
    }
    
    // Check for positive keywords
    if (lowerText.match(new RegExp(Object.keys(responses)[2]))) {
      return {
        text: responses["good|great|awesome|happy"],
        showActions: true
      };
    }
    
    // Default response
    return {
      text: responses["default"]
    };
  };

  // Handle sending a message
  const handleSend = () => {
    if (inputText.trim() === '') return;

    // Add user message
    const userMessage = {
      id: messages.length + 1,
      text: inputText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    // Simulate bot thinking
    setTimeout(() => {
      const botResponse = findResponse(inputText);
      
      const botMessage = {
        id: messages.length + 2,
        text: botResponse.text,
        sender: 'bot',
        timestamp: new Date(),
        showActions: botResponse.showActions || false,
        isCrisis: botResponse.isCrisis || false
      };

      setMessages(prev => [...prev, botMessage]);
    }, 500);
  };

  // Handle mood selection
  const handleMoodSelection = (mood) => {
    let responseText = '';
    
    if (mood.range === '1-3') {
      responseText = "Awesome! Let's maintain this positive energy";
    } else if (mood.range === '4-7') {
      responseText = "Let's work on improving your day";
    } else {
      responseText = "I'm here to support you. Let's find some help";
    }
    
    const userMessage = {
      id: messages.length + 1,
      text: `${mood.emoji} ${mood.label}`,
      sender: 'user',
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    
    setTimeout(() => {
      const botMessage = {
        id: messages.length + 2,
        text: responseText,
        sender: 'bot',
        timestamp: new Date(),
        showActions: true
      };
      
      setMessages(prev => [...prev, botMessage]);
    }, 500);
  };

  // Handle action button clicks
  const handleActionClick = (path) => {
    navigate(path);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  // Handle closing the educational banner
  const handleCloseBanner = () => {
    setShowEducationalBanner(false);
    setVisited();
  };

  return (
    <div style={{ 
      maxWidth: '480px', 
      margin: 'auto', 
      padding: '20px', 
      backgroundColor: '#f5f7fa', 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '60px'
    }}>
      <NavBar title="🤖 BridgeBot Chat" />
      
      {/* Educational Banner - Only shows on first visit */}
      {showEducationalBanner && (
        <EducationalBanner 
          feature="bridgebot" 
          onDismiss={handleCloseBanner} 
        />
      )}
      
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '20px',
        position: 'relative'
      }}>
        <h1 style={{ 
          color: '#2c3e50', 
          fontSize: '24px', 
          marginBottom: '4px' 
        }}>
          🤖 BridgeBot Chat
        </h1>
        <p style={{ 
          color: '#7f8c8d', 
          fontSize: '14px', 
          margin: 0 
        }}>
          Daily check-ins: {sessionCount}/3
        </p>
      </div>

      {/* Chat Container */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        marginBottom: '20px',
        padding: '10px'
      }}>
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              display: 'flex',
              justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '15px',
              animation: 'fadeIn 0.3s ease-in'
            }}
          >
            <div
              style={{
                maxWidth: '75%',
                padding: '12px 16px',
                borderRadius: message.sender === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                backgroundColor: message.sender === 'user' ? '#4361ee' : 
                               message.isCrisis ? '#f87171' : '#e2e8f0',
                color: message.sender === 'user' ? 'white' : 
                       message.isCrisis ? 'white' : '#2c3e50',
                wordWrap: 'break-word',
                textAlign: 'left',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {message.text}
              
              {/* Show action buttons after specific bot messages */}
              {message.showActions && (
                <div style={{ marginTop: '10px' }}>
                  {actionButtons.map((action, index) => (
                    <Button
                      key={index}
                      onClick={() => handleActionClick(action.path)}
                      variant="primary"
                      size="sm"
                      style={{
                        display: 'block',
                        width: '100%',
                        marginBottom: '8px'
                      }}
                    >
                      {action.text}
                    </Button>
                  ))}
                </div>
              )}
              
              {/* Show mood selection after welcome message */}
              {message.id === 1 && (
                <div style={{ marginTop: '15px' }}>
                  <p style={{ margin: '10px 0 10px 0', fontSize: '14px' }}>
                    How are you feeling today?
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                    {moodOptions.map((mood, index) => (
                      <Card
                        key={index}
                        hoverable
                        style={{
                          textAlign: 'center',
                          cursor: 'pointer',
                          padding: '15px 10px',
                          width: '30%',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0'
                        }}
                        onClick={() => handleMoodSelection(mood)}
                      >
                        <div style={{ fontSize: '28px', marginBottom: '5px' }}>
                          {mood.emoji}
                        </div>
                        <div style={{ fontSize: '12px' }}>
                          {mood.label}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{
        display: 'flex',
        borderTop: '1px solid #e2e8f0',
        paddingTop: '15px',
        paddingBottom: '10px'
      }}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          style={{
            flex: 1,
            padding: '12px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '24px',
            marginRight: '10px',
            outline: 'none',
            fontSize: '14px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05) inset'
          }}
        />
        <Button 
          onClick={handleSend} 
          variant="primary"
          style={{ 
            borderRadius: '24px',
            padding: '12px 20px',
            fontWeight: '600'
          }}
        >
          Send
        </Button>
      </div>
    </div>
  );
}

export default BridgeBotChat;